import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react"
import { PALETTE_IDS, isNamedPalette } from "../constants/palettes"

const STORAGE_KEY = "palette"
// The pre-deck key, which held only "dark" or "light". Read once so a returning
// visitor is not silently reset to the OS preference; writing STORAGE_KEY on
// the next effect completes the migration.
const LEGACY_KEY = "theme"

const ThemeContext = createContext({
  palette: "dark",
  mounted: false,
  setPalette: () => {},
  cyclePalette: () => {},
})

export const useTheme = () => useContext(ThemeContext)

/**
 * Apply a palette to the document. `light` is the existing `.light-mode`
 * class; every other named scheme is a data-palette attribute whose CSS block
 * outranks it.
 */
export const applyPalette = palette => {
  const root = document.documentElement
  root.classList.toggle("light-mode", palette === "light")
  if (isNamedPalette(palette)) root.setAttribute("data-palette", palette)
  else root.removeAttribute("data-palette")
}

export const ThemeProvider = ({ children }) => {
  const [palette, setPaletteState] = useState("dark")
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    // The pre-body script already applied this before paint; read back what it
    // chose so React state agrees with the DOM.
    let stored = null
    try {
      stored = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_KEY)
    } catch (e) {
      /* storage disabled */
    }
    if (stored && PALETTE_IDS.includes(stored)) {
      setPaletteState(stored)
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
      setPaletteState(prefersDark ? "dark" : "light")
    }
  }, [])

  useEffect(() => {
    if (!mounted) return
    applyPalette(palette)
    try {
      localStorage.setItem(STORAGE_KEY, palette)
    } catch (e) {
      /* storage disabled */
    }
  }, [palette, mounted])

  const setPalette = useCallback(next => {
    if (PALETTE_IDS.includes(next)) setPaletteState(next)
  }, [])

  const cyclePalette = useCallback(() => {
    setPaletteState(current => {
      const i = PALETTE_IDS.indexOf(current)
      return PALETTE_IDS[(i + 1) % PALETTE_IDS.length]
    })
  }, [])

  return (
    <ThemeContext.Provider value={{ palette, mounted, setPalette, cyclePalette }}>
      {children}
    </ThemeContext.Provider>
  )
}
